import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaChevronDown, FaArrowRight } from 'react-icons/fa';
import PropTypes from 'prop-types';

const MegaMenu = ({ trigger, categories = [], className = '', onCategoryClick }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState(null);
  const menuRef = useRef(null);
  const timeoutRef = useRef(null);

  // Default categories structure
  const defaultCategories = [
    {
      id: 'women',
      name: 'Women',
      featured: [
        {
          name: 'New Arrivals',
          path: '/shop?category=women&filter=new',
          image: '/assets/women-new.jpg',
        },
        {
          name: 'Best Sellers',
          path: '/shop?category=women&filter=bestsellers',
          image: '/assets/women-bestsellers.jpg',
        },
        {
          name: 'Sale Items',
          path: '/shop?category=women&filter=sale',
          image: '/assets/women-sale.jpg',
        },
      ],
      subcategories: [
        {
          name: 'Clothing',
          items: [
            { name: 'Dresses', path: '/shop?category=dresses' },
            { name: 'Tops & Blouses', path: '/shop?category=tops' },
            { name: 'Pants & Jeans', path: '/shop?category=bottoms' },
            { name: 'Skirts', path: '/shop?category=skirts' },
            { name: 'Jackets & Coats', path: '/shop?category=outerwear' },
          ],
        },
        {
          name: 'Accessories',
          items: [
            { name: 'Bags & Purses', path: '/shop?category=bags' },
            { name: 'Jewelry', path: '/shop?category=jewelry' },
            { name: 'Scarves', path: '/shop?category=scarves' },
            { name: 'Belts', path: '/shop?category=belts' },
          ],
        },
        {
          name: 'Shoes',
          items: [
            { name: 'Heels', path: '/shop?category=heels' },
            { name: 'Flats', path: '/shop?category=flats' },
            { name: 'Sneakers', path: '/shop?category=sneakers' },
            { name: 'Boots', path: '/shop?category=boots' },
          ],
        },
      ],
    },
    {
      id: 'men',
      name: 'Men',
      featured: [
        {
          name: 'New Arrivals',
          path: '/shop?category=men&filter=new',
          image: '/assets/men-new.jpg',
        },
        {
          name: 'Best Sellers',
          path: '/shop?category=men&filter=bestsellers',
          image: '/assets/men-bestsellers.jpg',
        },
        {
          name: 'Sale Items',
          path: '/shop?category=men&filter=sale',
          image: '/assets/men-sale.jpg',
        },
      ],
      subcategories: [
        {
          name: 'Clothing',
          items: [
            { name: 'Shirts', path: '/shop?category=shirts' },
            { name: 'T-Shirts', path: '/shop?category=tshirts' },
            { name: 'Pants', path: '/shop?category=pants' },
            { name: 'Jeans', path: '/shop?category=jeans' },
            { name: 'Jackets', path: '/shop?category=jackets' },
          ],
        },
        {
          name: 'Accessories',
          items: [
            { name: 'Watches', path: '/shop?category=watches' },
            { name: 'Wallets', path: '/shop?category=wallets' },
            { name: 'Ties', path: '/shop?category=ties' },
            { name: 'Belts', path: '/shop?category=belts' },
          ],
        },
        {
          name: 'Shoes',
          items: [
            { name: 'Dress Shoes', path: '/shop?category=dress-shoes' },
            { name: 'Sneakers', path: '/shop?category=sneakers' },
            { name: 'Boots', path: '/shop?category=boots' },
            { name: 'Sandals', path: '/shop?category=sandals' },
          ],
        },
      ],
    },
    {
      id: 'accessories',
      name: 'Accessories',
      featured: [
        {
          name: 'Trending Now',
          path: '/shop?category=accessories&filter=trending',
          image: '/assets/accessories-trending.jpg',
        },
        {
          name: 'Gift Ideas',
          path: '/shop?category=accessories&filter=gifts',
          image: '/assets/accessories-gifts.jpg',
        },
      ],
      subcategories: [
        {
          name: 'Bags',
          items: [
            { name: 'Handbags', path: '/shop?category=handbags' },
            { name: 'Backpacks', path: '/shop?category=backpacks' },
            { name: 'Clutches', path: '/shop?category=clutches' },
            { name: 'Tote Bags', path: '/shop?category=tote-bags' },
          ],
        },
        {
          name: 'Jewelry',
          items: [
            { name: 'Necklaces', path: '/shop?category=necklaces' },
            { name: 'Earrings', path: '/shop?category=earrings' },
            { name: 'Bracelets', path: '/shop?category=bracelets' },
            { name: 'Rings', path: '/shop?category=rings' },
          ],
        },
      ],
    },
  ];

  const menuCategories = categories.length > 0 ? categories : defaultCategories;

  useEffect(() => {
    const handleClickOutside = event => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
        setActiveCategory(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleMouseEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setIsOpen(true);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setIsOpen(false);
      setActiveCategory(null);
    }, 150);
  };

  const handleCategoryHover = categoryId => {
    setActiveCategory(categoryId);
  };

  const handleItemClick = item => {
    setIsOpen(false);
    setActiveCategory(null);
    onCategoryClick?.(item);
  };

  return (
    <div
      ref={menuRef}
      className={`relative ${className}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Trigger */}
      <button
        className='flex items-center space-x-1 px-3 py-2 text-white hover:text-orange-400 transition-colors'
        aria-expanded={isOpen}
        aria-haspopup='true'
      >
        <span>{trigger}</span>
        <FaChevronDown
          className={`w-3 h-3 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {/* Mega Menu Dropdown */}
      {isOpen && (
        <div className='absolute top-full left-0 w-screen max-w-6xl bg-white border border-neutral-200 shadow-xl z-50 rounded-lg mt-1'>
          <div className='flex'>
            {/* Category Navigation */}
            <div className='w-48 bg-neutral-50 border-r border-neutral-200 rounded-l-lg'>
              <div className='p-4'>
                <h3 className='text-sm font-semibold text-neutral-900 mb-3'>Categories</h3>
                <nav className='space-y-1'>
                  {menuCategories.map(category => (
                    <button
                      key={category.id}
                      onMouseEnter={() => handleCategoryHover(category.id)}
                      className={`w-full text-left px-3 py-2 text-sm rounded-md transition-colors flex items-center justify-between ${
                        activeCategory === category.id
                          ? 'bg-primary-100 text-primary-700'
                          : 'text-neutral-700 hover:bg-neutral-100'
                      }`}
                    >
                      <span>{category.name}</span>
                      <FaArrowRight className='w-3 h-3' />
                    </button>
                  ))}
                </nav>
              </div>
            </div>

            {/* Category Content */}
            <div className='flex-1 p-6'>
              {activeCategory &&
                (() => {
                  const category = menuCategories.find(cat => cat.id === activeCategory);
                  if (!category) {
                    return null;
                  }

                  return (
                    <div className='grid grid-cols-12 gap-6'>
                      {/* Subcategories */}
                      <div className='col-span-8'>
                        <div className='grid grid-cols-3 gap-6'>
                          {category.subcategories?.map(subcategory => (
                            <div key={subcategory.name}>
                              <h4 className='font-semibold text-neutral-900 mb-3'>
                                {subcategory.name}
                              </h4>
                              <ul className='space-y-2'>
                                {subcategory.items.map(item => (
                                  <li key={item.name}>
                                    <Link
                                      to={item.path}
                                      onClick={() => handleItemClick(item)}
                                      className='text-sm text-neutral-600 hover:text-primary-600 transition-colors'
                                    >
                                      {item.name}
                                    </Link>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Featured Items */}
                      <div className='col-span-4'>
                        <h4 className='font-semibold text-neutral-900 mb-3'>Featured</h4>
                        <div className='space-y-3'>
                          {category.featured?.map(item => (
                            <Link
                              key={item.name}
                              to={item.path}
                              onClick={() => handleItemClick(item)}
                              className='block group'
                            >
                              <div className='flex items-center space-x-3 p-3 rounded-lg hover:bg-neutral-50 transition-colors'>
                                <div className='w-12 h-12 bg-neutral-200 rounded-lg flex-shrink-0 overflow-hidden'>
                                  {item.image && (
                                    <img
                                      src={item.image}
                                      alt={item.name}
                                      className='w-full h-full object-cover group-hover:scale-105 transition-transform duration-200'
                                      onError={e => {
                                        e.target.style.display = 'none';
                                      }}
                                    />
                                  )}
                                </div>
                                <div>
                                  <div className='font-medium text-neutral-900 group-hover:text-primary-600 transition-colors'>
                                    {item.name}
                                  </div>
                                  <div className='text-xs text-neutral-500'>Shop now</div>
                                </div>
                              </div>
                            </Link>
                          ))}
                        </div>
                      </div>
                    </div>
                  );
                })()}

              {!activeCategory && (
                <div className='text-center py-12'>
                  <div className='text-neutral-400 mb-2'>
                    <FaArrowRight className='w-8 h-8 mx-auto' />
                  </div>
                  <p className='text-neutral-500'>Hover over a category to see options</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

MegaMenu.propTypes = {
  trigger: PropTypes.string.isRequired,
  categories: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      featured: PropTypes.arrayOf(
        PropTypes.shape({
          name: PropTypes.string.isRequired,
          path: PropTypes.string.isRequired,
          image: PropTypes.string,
        }),
      ),
      subcategories: PropTypes.arrayOf(
        PropTypes.shape({
          name: PropTypes.string.isRequired,
          items: PropTypes.arrayOf(
            PropTypes.shape({
              name: PropTypes.string.isRequired,
              path: PropTypes.string.isRequired,
            }),
          ).isRequired,
        }),
      ),
    }),
  ),
  className: PropTypes.string,
  onCategoryClick: PropTypes.func,
};

export default MegaMenu;
